import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const detail = (e) => { try { return JSON.parse(e?.response?.data || "{}").detail; } catch { return null; } };

// One realtime WebRTC call to the AI interviewer; reconnect() starts a new call on the same mic track.
export function useLiveCall(token, { onLine, onEndRequested, onRemote }) {
  const [status, setStatus] = useState("idle");
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const pc = useRef(null);
  const dc = useRef(null);
  const audio = useRef(null);
  const ending = useRef(false);
  const cb = useRef({ onLine, onEndRequested, onRemote });
  cb.current = { onLine, onEndRequested, onRemote };

  const send = useCallback((ev) => { if (dc.current?.readyState === "open") dc.current.send(JSON.stringify(ev)); }, []);
  const system = useCallback((text, respond) => {
    send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text }] } });
    if (respond) send({ type: "response.create" });
  }, [send]);

  const onEvent = useCallback((raw) => {
    let ev;
    try { ev = JSON.parse(raw); } catch { return; }
    const t = ev.type;
    if (t === "conversation.item.input_audio_transcription.completed") cb.current.onLine({ id: ev.item_id, speaker: "candidate", text: ev.transcript || "" });
    else if (t === "response.output_audio_transcript.done" || t === "response.audio_transcript.done") cb.current.onLine({ id: `${ev.item_id}:${ev.content_index || 0}`, speaker: "ai", text: ev.transcript || "" });
    else if (t === "output_audio_buffer.started") setAiSpeaking(true);
    else if (t === "output_audio_buffer.stopped" || t === "output_audio_buffer.cleared") { setAiSpeaking(false); if (ending.current) setTimeout(() => cb.current.onEndRequested(), 600); }
    else if (t === "input_audio_buffer.speech_started") setUserSpeaking(true);
    else if (t === "input_audio_buffer.speech_stopped") setUserSpeaking(false);
    else if (t === "response.function_call_arguments.done" && ev.name === "end_interview") {
      ending.current = true;
      setTimeout(() => cb.current.onEndRequested(), 9000);
    }
  }, []);

  const close = useCallback(() => {
    try { dc.current?.close(); } catch { /* closed */ }
    try { pc.current?.close(); } catch { /* closed */ }
    dc.current = null; pc.current = null;
    setAiSpeaking(false);
  }, []);

  const connect = useCallback(async (micTrack, micStream) => {
    close();
    setStatus("connecting"); setError(null);
    const conn = new RTCPeerConnection();
    pc.current = conn;
    if (!audio.current) { audio.current = new Audio(); audio.current.autoplay = true; }
    conn.ontrack = (e) => { audio.current.srcObject = e.streams[0]; audio.current.play().catch(() => {}); cb.current.onRemote?.(e.streams[0]); };
    conn.addTrack(micTrack, micStream);
    const channel = conn.createDataChannel("oai-events");
    dc.current = channel;
    channel.onopen = () => send({ type: "response.create" });
    channel.onmessage = (m) => onEvent(m.data);
    let lost;
    conn.onconnectionstatechange = () => {
      if (pc.current !== conn) return;
      const s = conn.connectionState;
      if (s === "connected") { clearTimeout(lost); setStatus("live"); }
      else if (s === "failed") setStatus("dropped");
      else if (s === "disconnected") { setStatus("reconnecting"); lost = setTimeout(() => { if (pc.current === conn && conn.connectionState !== "connected") setStatus("dropped"); }, 6000); }
    };
    try {
      const offer = await conn.createOffer();
      await conn.setLocalDescription(offer);
      const r = await api.post(`/public/interview/${token}/live/connect`, offer.sdp, { headers: { "Content-Type": "application/sdp" }, responseType: "text", transformRequest: [(d) => d], timeout: 30000 });
      if (pc.current !== conn) return null;
      await conn.setRemoteDescription({ type: "answer", sdp: r.data });
      return { remaining: Number(r.headers["x-remaining-seconds"] || 0), resumed: r.headers["x-resumed"] === "1" };
    } catch (e) {
      if (pc.current === conn) { setError(detail(e) || "We couldn't connect to the AI interviewer."); setStatus(e?.response?.status === 409 ? "timeup" : "dropped"); }
      return null;
    }
  }, [token, close, send, onEvent]);

  useEffect(() => () => { close(); if (audio.current) audio.current.srcObject = null; }, [close]);
  return { status, setStatus, aiSpeaking, userSpeaking, error, connect, close, system, remoteAudio: audio };
}
